// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import * as GuildMemberCommands from '@app/features/member/commands/GuildMemberCommands';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {User} from '@app/features/user/models/User';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';
import styles from './RemoveTimeoutModal.module.css';

const logger = new Logger('RemoveTimeoutModal');
interface RemoveTimeoutModalProps {
	guildId: string;
	targetUser: User;
}
export const RemoveTimeoutModal: React.FC<RemoveTimeoutModalProps> = observer(({guildId, targetUser}) => {
	const {t} = useLingui();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const handleRemove = async () => {
		setIsSubmitting(true);
		try {
			await GuildMemberCommands.timeout(guildId, targetUser.id, null);
			ToastCommands.createToast({
				type: 'success',
				children: <Trans>Successfully removed timeout from {targetUser.tag}</Trans>,
			});
			ModalCommands.pop();
		} catch (error) {
			logger.error('Failed to remove timeout:', error);
			ToastCommands.createToast({
				type: 'error',
				children: <Trans>Failed to remove timeout. Please try again.</Trans>,
			});
		} finally {
			setIsSubmitting(false);
		}
	};
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`Remove Timeout`} />
			<Modal.Content>
				<p className={styles.description}>
					<Trans>
						Removing the timeout will allow <strong>{targetUser.tag}</strong> to send messages, react, and join voice
						channels again.
					</Trans>
				</p>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={() => ModalCommands.pop()} disabled={isSubmitting}>
					<Trans>Cancel</Trans>
				</Button>
				<Button variant="danger-primary" onClick={handleRemove} disabled={isSubmitting}>
					<Trans>Remove Timeout</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
